import { forwardRef, useEffect, useId, useMemo, useRef, useState } from 'react'
import type {
  ChangeEvent,
  FocusEvent,
  InputHTMLAttributes,
  KeyboardEvent,
  MutableRefObject,
} from 'react'
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react'
import { cx } from '@shared/lib/cx'
import { FieldFeedback } from '../Field/FieldFeedback'
import { resolveFieldFeedback } from '../Field/field-feedback'
import type { FieldStatus } from '../Field/field-feedback'
import {
  actionButton,
  control,
  dayButton,
  daysGrid,
  field,
  footer,
  header,
  hiddenInput,
  iconButton,
  input,
  label as labelClass,
  labelInvalid,
  monthLabel,
  navButtons,
  popover,
  weekday,
  weekdayGrid,
} from './DateField.css'

const monthFormatter = new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' })
const weekdays = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S']

export type DateFieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  label?: string
  helperText?: string
  error?: string
  status?: FieldStatus
  message?: string
}

type CalendarDay = {
  iso: string
  label: number
  outside: boolean
  disabled: boolean
}

function pad(value: number) {
  return String(value).padStart(2, '0')
}

function toIso(year: number, monthIndex: number, day: number) {
  return `${year}-${pad(monthIndex + 1)}-${pad(day)}`
}

function parseIso(value: unknown) {
  if (typeof value !== 'string') return null
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) return null
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const date = new Date(year, month - 1, day)

  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null
  }

  return date
}

function parseDisplay(value: string) {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value)
  if (!match) return null
  const day = Number(match[1])
  const month = Number(match[2])
  const year = Number(match[3])
  const date = new Date(year, month - 1, day)

  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null
  }

  return toIso(year, month - 1, day)
}

function formatDisplay(iso: string) {
  const date = parseIso(iso)
  if (!date) return ''
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`
}

function maskDate(value: string) {
  const digits = value.replace(/\D/g, '').slice(0, 8)
  const parts = [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)].filter(Boolean)
  return parts.join('/')
}

function isoFromDate(date: Date) {
  return toIso(date.getFullYear(), date.getMonth(), date.getDate())
}

function isOutOfRange(iso: string, min?: string | number, max?: string | number) {
  const minIso = typeof min === 'string' ? min : ''
  const maxIso = typeof max === 'string' ? max : ''
  return Boolean((minIso && iso < minIso) || (maxIso && iso > maxIso))
}

function getMonthDays(
  monthDate: Date,
  min?: string | number,
  max?: string | number,
): CalendarDay[] {
  const year = monthDate.getFullYear()
  const month = monthDate.getMonth()
  const firstDay = new Date(year, month, 1)
  const start = new Date(year, month, 1 - firstDay.getDay())

  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(start)
    date.setDate(start.getDate() + index)
    const iso = isoFromDate(date)
    return {
      iso,
      label: date.getDate(),
      outside: date.getMonth() !== month,
      disabled: Boolean(isOutOfRange(iso, min, max)),
    }
  })
}

function setForwardedRef<T>(ref: React.ForwardedRef<T>, value: T) {
  if (typeof ref === 'function') {
    ref(value)
    return
  }

  if (ref) {
    ;(ref as MutableRefObject<T>).current = value
  }
}

export const DateField = forwardRef<HTMLInputElement, DateFieldProps>(function DateField(
  {
    label,
    helperText,
    error,
    status,
    message,
    className,
    id,
    value,
    defaultValue,
    onChange,
    onBlur,
    name,
    disabled,
    required,
    placeholder = 'dd/mm/aaaa',
    min,
    max,
    autoFocus,
    autoComplete = 'off',
    ...rest
  },
  ref,
) {
  const generatedId = useId()
  const inputId = id ?? generatedId
  const popoverId = `${inputId}-calendar`
  const feedbackId = `${inputId}-feedback`
  const fieldRef = useRef<HTMLDivElement | null>(null)
  const hiddenRef = useRef<HTMLInputElement | null>(null)
  const controlled = value !== undefined
  const initialIso = (controlled ? value : defaultValue)?.toString() ?? ''
  const [internalIso, setInternalIso] = useState(parseIso(initialIso) ? initialIso : '')
  const selectedIso = controlled ? (value?.toString() ?? '') : internalIso
  const [displayValue, setDisplayValue] = useState(formatDisplay(selectedIso))
  const [open, setOpen] = useState(false)
  const [monthDate, setMonthDate] = useState(() => parseIso(selectedIso) ?? new Date())
  const feedback = resolveFieldFeedback({ error, helperText, message, status })
  const describedBy = cx(rest['aria-describedby'], feedback.message && feedbackId)
  const todayIso = isoFromDate(new Date())
  const monthDays = useMemo(() => getMonthDays(monthDate, min, max), [max, min, monthDate])

  useEffect(() => {
    setDisplayValue(formatDisplay(selectedIso))
    const selectedDate = parseIso(selectedIso)
    if (selectedDate) {
      setMonthDate(selectedDate)
    }
  }, [selectedIso])

  useEffect(() => {
    if (!open) return

    function handlePointerDown(event: PointerEvent) {
      if (!fieldRef.current?.contains(event.target as Node)) {
        setOpen(false)
      }
    }

    document.addEventListener('pointerdown', handlePointerDown)
    return () => document.removeEventListener('pointerdown', handlePointerDown)
  }, [open])

  function getEventTarget(nextValue = selectedIso) {
    if (hiddenRef.current) {
      hiddenRef.current.value = nextValue
      return hiddenRef.current
    }

    return { name, value: nextValue } as HTMLInputElement
  }

  function emitChange(nextIso: string) {
    if (!controlled) {
      setInternalIso(nextIso)
    }
    onChange?.({
      target: getEventTarget(nextIso),
      currentTarget: getEventTarget(nextIso),
    } as ChangeEvent<HTMLInputElement>)
  }

  function commitIso(nextIso: string) {
    if (disabled || isOutOfRange(nextIso, min, max)) return
    setDisplayValue(formatDisplay(nextIso))
    emitChange(nextIso)
    setOpen(false)
  }

  function handleDisplayChange(event: ChangeEvent<HTMLInputElement>) {
    const nextDisplay = maskDate(event.target.value)
    setDisplayValue(nextDisplay)

    if (nextDisplay === '') {
      emitChange('')
      return
    }

    if (nextDisplay.length === 10) {
      const nextIso = parseDisplay(nextDisplay)
      if (nextIso && !isOutOfRange(nextIso, min, max)) {
        emitChange(nextIso)
        setMonthDate(parseIso(nextIso) ?? monthDate)
      }
    }
  }

  function handleBlur(event: FocusEvent<HTMLDivElement>) {
    if (event.currentTarget.contains(event.relatedTarget)) return

    const nextIso = parseDisplay(displayValue)
    if (displayValue && (!nextIso || isOutOfRange(nextIso, min, max))) {
      setDisplayValue(formatDisplay(selectedIso))
    }

    setOpen(false)
    onBlur?.({
      target: getEventTarget(),
      currentTarget: getEventTarget(),
    } as FocusEvent<HTMLInputElement>)
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (disabled) return

    if (event.key === 'Escape') {
      setOpen(false)
      return
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setOpen(true)
    }
  }

  function changeMonth(delta: number) {
    setMonthDate((current) => new Date(current.getFullYear(), current.getMonth() + delta, 1))
  }

  function clearDate() {
    setDisplayValue('')
    emitChange('')
    setOpen(false)
  }

  const todayDisabled = isOutOfRange(todayIso, min, max)

  return (
    <div ref={fieldRef} className={cx(field, className)} onBlur={handleBlur}>
      {label ? (
        <label htmlFor={inputId} className={cx(labelClass, feedback.invalid && labelInvalid)}>
          {label}
        </label>
      ) : null}
      <div className={control({ invalid: feedback.invalid, disabled: Boolean(disabled) })}>
        <input
          {...rest}
          id={inputId}
          className={input}
          value={displayValue}
          placeholder={placeholder}
          inputMode="numeric"
          autoComplete={autoComplete}
          autoCorrect="off"
          spellCheck={false}
          aria-controls={popoverId}
          aria-describedby={describedBy || undefined}
          aria-expanded={open}
          aria-haspopup="dialog"
          aria-invalid={feedback.invalid}
          disabled={disabled}
          required={required}
          autoFocus={autoFocus}
          onChange={handleDisplayChange}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
        />
        <input
          ref={(element) => {
            hiddenRef.current = element
            setForwardedRef(ref, element)
          }}
          className={hiddenInput}
          tabIndex={-1}
          aria-hidden="true"
          name={name}
          value={selectedIso}
          required={required}
          readOnly
        />
        <button
          type="button"
          className={iconButton}
          aria-label="Abrir calendário"
          disabled={disabled}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => setOpen((current) => !current)}
        >
          <CalendarDays size={16} aria-hidden />
        </button>
      </div>
      {open ? (
        <div id={popoverId} className={popover} role="dialog" aria-label="Selecionar data">
          <div className={header}>
            <span className={monthLabel}>{monthFormatter.format(monthDate)}</span>
            <span className={navButtons}>
              <button
                type="button"
                className={iconButton}
                aria-label="Mês anterior"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => changeMonth(-1)}
              >
                <ChevronLeft size={16} aria-hidden />
              </button>
              <button
                type="button"
                className={iconButton}
                aria-label="Próximo mês"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => changeMonth(1)}
              >
                <ChevronRight size={16} aria-hidden />
              </button>
            </span>
          </div>
          <div className={weekdayGrid} aria-hidden>
            {weekdays.map((day, index) => (
              <span key={`${day}-${index}`} className={weekday}>
                {day}
              </span>
            ))}
          </div>
          <div className={daysGrid} role="grid" aria-label={monthFormatter.format(monthDate)}>
            {monthDays.map((day) => (
              <button
                key={day.iso}
                type="button"
                role="gridcell"
                className={dayButton({
                  outside: day.outside,
                  today: day.iso === todayIso,
                  selected: day.iso === selectedIso,
                })}
                aria-selected={day.iso === selectedIso}
                disabled={day.disabled}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => commitIso(day.iso)}
              >
                {day.label}
              </button>
            ))}
          </div>
          <div className={footer}>
            <button
              type="button"
              className={actionButton}
              onMouseDown={(event) => event.preventDefault()}
              onClick={clearDate}
            >
              Limpar
            </button>
            <button
              type="button"
              className={actionButton}
              disabled={todayDisabled}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => commitIso(todayIso)}
            >
              Hoje
            </button>
          </div>
        </div>
      ) : null}
      <FieldFeedback id={feedbackId} status={feedback.status}>
        {feedback.message}
      </FieldFeedback>
    </div>
  )
})
